import { useEffect, useState } from "react";
import dayjs from "dayjs";

import { fetchCuringAtPoint, useCuringEstimate } from "../../api/curing";
import { useSimulation } from "../../contexts/SimulationContext";
import type { CuringResponse } from "../../types/curing";
import ToolbarButton from "../toolbar/ToolbarButton";
import CuringPointPopover, { CuringPointReading } from "./CuringPointPopover";
import { mapEventScreenPoint } from "../../utils/uiZoom";

/**
 * Grass curing overlay: how much of the grass across the project has died off
 * and is available to burn.
 *
 * Landscape context rather than a simulation result, so it sits with the
 * vegetation and fuel layers and is available whether or not a simulation has
 * been run. The image is the estimate current at the date being simulated --
 * for a reconstruction of last month's fire that is last month's field, not
 * today's -- and is drawn over that estimate's own bounds, which may differ
 * from the project bbox if the project has since been expanded.
 *
 * Where no estimate describes the date (a seasonal run months out, a date
 * before the first estimate) there is nothing to draw: showing today's field
 * there would be the silent extrapolation the hold-forward rule forbids.
 */
/**
 * Why there is nothing to draw for a date, in the user's terms.
 *
 * Readings are kept for good; what is limited is how far a reading is
 * carried forward. So the message names the reading that would have covered
 * the date and why it does not, rather than implying the data has gone.
 */
const explainMissingCuring = (
  dated: NonNullable<CuringResponse["for_date"]>,
  forDate?: string,
): string => {
  const day = dayjs(forDate ?? new Date());
  const label = day.format("D MMM YYYY");
  const reading = dated.observed_on ? dayjs(dated.observed_on).format("D MMM YYYY") : null;
  const inFuture = day.isAfter(dayjs(), "day");

  switch (dated.reason) {
    case "beyond_hold_window":
      return inFuture
        ? `No curing data for ${label}: the latest reading is from ${reading}, and a reading is used for the fortnight after it.`
        : `No curing data for ${label}: the nearest reading before it is from ${reading}, more than a fortnight earlier.`;
    case "no_estimate_before_date":
      return `No curing data for ${label}: this project's readings start after that date.`;
    case "bounds_changed":
      return "The project has been expanded since the last reading. A new one is on its way.";
    case "estimate_has_no_value":
      return `The reading for ${label} has no usable value — cloud over the whole project.`;
    default:
      return `No curing data for ${label}.`;
  }
};

const GrassCuringLayer = (id: string, name: string, projectId?: number | string) => {
  const [active, setActive] = useState(false);
  // Curing is a backdrop the user reads other layers against, so how
  // strongly it paints is a per-user judgement, not a fixed constant.
  const [opacity, setOpacity] = useState(0.95);
  const [popoverPosition, setPopoverPosition] = useState<
    { top: number; left: number } | null
  >(null);
  const [reading, setReading] = useState<CuringPointReading | null>(null);
  // Mapbox image-source shape, matching the other raster overlays.
  const [rasterSource, setRasterSource] = useState<{
    type: "image";
    url: string;
    coordinates: number[][];
  } | null>(null);
  const [stale, setStale] = useState(false);

  // The date the map is being read for: the run's own start date once one
  // has been chosen, today until then. Taken from the string, not through
  // the browser's clock, because the string is already in the project's
  // timezone and a conversion could shift it across midnight.
  const { simulationParams } = useSimulation();
  const startTime = simulationParams?.startTime;
  const forDate = startTime ? startTime.slice(0, 10) : undefined;

  const { data: curing } = useCuringEstimate(projectId, forDate);
  const dated = curing?.available ? curing.for_date : undefined;
  const bounds = dated?.within_window ? dated.bounds : undefined;
  const url = dated?.within_window ? dated.png_url : undefined;

  const paintedCells = curing?.estimate?.summary?.painted_cells;

  let unavailableReason: string | undefined;
  if (curing && !curing.available) {
    unavailableReason = "No curing estimate for this project yet.";
  } else if (curing && dated && !dated.within_window) {
    unavailableReason = explainMissingCuring(dated, forDate);
  } else if (paintedCells === 0) {
    // Mapped, and none of it carries grass. The layer would switch on and
    // draw nothing, which reads as broken rather than as an answer.
    unavailableReason =
      "Nothing here cures: none of this project's fuel has a grass component.";
  }

  useEffect(() => {
    if (!bounds || !url || paintedCells === 0) {
      setRasterSource(null);
      return;
    }

    const { top, left, bottom, right } = bounds;
    setRasterSource({
      type: "image",
      url,
      coordinates: [
        [left, top],
        [right, top],
        [right, bottom],
        [left, bottom],
      ],
    });
    setStale(true);
  }, [url, bounds, paintedCells]);

  const rasterLayerStyle = {
    id: id,
    type: "raster",
    source: rasterSource,
    paint: {
      "raster-opacity": opacity,
      "raster-fade-duration": 0,
    },
  };

  // Raster layers cannot be queried for features, so the map dispatches the
  // click straight to us and we sample the stored raster server-side.
  const handleMapClick = (event: {
    lngLat?: { lat: number; lng: number };
    point?: { x: number; y: number };
    originalEvent?: Event;
    target?: { getContainer(): HTMLElement };
  }) => {
    if (!projectId || !rasterSource) return false;
    const lngLat = event?.lngLat;
    const point = event?.point;
    if (!lngLat || !point) return false;
    // Resolve the anchor now: e.point is layout px relative to the canvas,
    // MUI wants a screen-px viewport position.
    const anchor = mapEventScreenPoint({ point, originalEvent: event.originalEvent, target: event.target });

    // Only open on a pixel the overlay actually paints. The estimate covers
    // the whole project, so forest has a curing value behind it -- but
    // reporting one where the map shows nothing would be describing something
    // the user cannot see. Hence the read happens first and the popover opens
    // only if there is something to show.
    fetchCuringAtPoint(projectId, lngLat.lat, lngLat.lng)
      .then((result) => {
        if (!result.available) return;
        setReading(result);
        setPopoverPosition(anchor);
      })
      .catch(() => {
        /* A failed read is not worth interrupting the user for. */
      });
    return true;
  };

  const toolbarButton = (
    <ToolbarButton
      key={id}
      id={id}
      name={name}
      toggledOn={active}
      enabled={!!rasterSource}
      onClick={() => setActive(!active)}
      icon={"grass"}
    />
  );

  return {
    id: id,
    name: name,
    type: "raster",
    source: rasterSource,
    layerStyle: rasterLayerStyle,
    toolbarButton: toolbarButton,
    // Switched on but with nothing to draw for this date is still off: the
    // choice survives, so moving the date back inside the window brings the
    // overlay straight back.
    active: active && !!rasterSource,
    unavailableReason,
    stale: stale,
    setStale: setStale,
    opacity: opacity,
    setOpacity: setOpacity,
    onClick: handleMapClick,
    interactiveLayerIds: active && rasterSource ? [id] : [],
    // Click anywhere on the overlay to read the curing there. This replaces a
    // static legend: the colour key is only useful as an answer about the
    // place being looked at.
    popover: active && rasterSource ? (
      <CuringPointPopover
        anchorPosition={popoverPosition}
        onClose={() => setPopoverPosition(null)}
        reading={reading}
      />
    ) : null,
  };
};

export default GrassCuringLayer;
